import { useState, useMemo, useEffect } from 'react';
import { getAnimeStatus, getAnimeRank, getAllRatings } from '../utils/animeUtils';

// アニメフィルタリング用のカスタムフック
export const useAnimeFilters = (animes, t = null) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('all');
  const [selectedYear, setSelectedYear] = useState(() => {
    const years = [...new Set(animes.map(a => a.year).filter(y => y))].sort((a, b) => b - a);
    return years.length > 0 ? years[0] : new Date().getFullYear();
  });
  const [selectedSeason, setSelectedSeason] = useState(() => {
    // Use translation function if available, otherwise default to spring character
    return t ? t('springValue') : '春';
  });
  const [filterRank, setFilterRank] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('none');
  const [activeFilter, setActiveFilter] = useState(null);

  // 年のリストを取得
  const years = useMemo(() => {
    return [...new Set(animes.map(a => a.year).filter(y => y))].sort((a, b) => b - a);
  }, [animes]);

  // 年リストが変更されたときに選択年を更新
  useEffect(() => {
    if (years.length > 0 && !years.includes(selectedYear)) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  // 全評価値を取得
  const allRatings = useMemo(() => getAllRatings(animes), [animes]);

  // サジェスト用のリスト
  const suggestionsList = useMemo(() => {
    const kw = searchTerm.toLowerCase().trim();
    if (!kw) return [];
    
    const pool = [];
    animes.forEach(a => {
      pool.push({ text: a.title, category: 'title' });
      if (a.studio) pool.push({ text: a.studio, category: 'studio' });
      if (a.director) pool.push({ text: a.director, category: 'director' });
      a.favoriteCharacters?.forEach(c => {
        if (c.voiceActor) pool.push({ text: c.voiceActor, category: 'voiceActor' });
      });
    });
    
    // 重複排除
    const uniq = new Map();
    pool.forEach(o => uniq.set(`${o.category}-${o.text}`, o));
    return [...uniq.values()]
      .filter(o => o.text.toLowerCase().includes(kw) && o.text.toLowerCase() !== kw)
      .slice(0, 10);
  }, [searchTerm, animes]);

  // フィルタリングされたアニメリスト
  const filteredAnimes = useMemo(() => {
    const kw = searchTerm.toLowerCase();
    let filtered = animes.filter(anime => {
      const inChars = anime.favoriteCharacters?.some(c => 
        c.voiceActor?.toLowerCase().includes(kw)
      );
      return anime.title.toLowerCase().includes(kw) ||
             (anime.studio || '').toLowerCase().includes(kw) ||
             (anime.director || '').toLowerCase().includes(kw) || 
             inChars;
    });

    if (viewMode === 'year') {
      filtered = filtered.filter(anime =>
        anime.year && !isNaN(anime.year) && Number(anime.year) === Number(selectedYear)
      );
    } else if (viewMode === 'season') {
      filtered = filtered.filter(anime =>
        anime.year && !isNaN(anime.year) && Number(anime.year) === Number(selectedYear) &&
        anime.season && anime.season === selectedSeason
      );
    }

    if (filterRank !== 'all') {
      filtered = filtered.filter(anime => {
        const rank = getAnimeRank(anime, allRatings);
        return rank === filterRank;
      });
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(anime => {
        const status = getAnimeStatus(anime);
        return filterStatus === status || (filterStatus === 'watching' && status === 'not-started');
      });
    }

    // ソート処理
    if (sortBy !== 'none') {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'rankAsc': {
            const rankA = getAnimeRank(a, allRatings);
            const rankB = getAnimeRank(b, allRatings);
            const rankOrder = { 'S+': 7, 'S': 6, 'A': 5, 'B': 4, 'C': 3, 'C-': 2, 'D': 1 };
            const valueA = rankOrder[rankA] || 0;
            const valueB = rankOrder[rankB] || 0;
            return valueA - valueB;
          }
          case 'rankDesc': {
            const rankA = getAnimeRank(a, allRatings);
            const rankB = getAnimeRank(b, allRatings);
            const rankOrder = { 'S+': 7, 'S': 6, 'A': 5, 'B': 4, 'C': 3, 'C-': 2, 'D': 1 };
            const valueA = rankOrder[rankA] || 0;
            const valueB = rankOrder[rankB] || 0;
            return valueB - valueA;
          }
          case 'yearAsc':
            return (a.year || 0) - (b.year || 0);
          case 'yearDesc':
            return (b.year || 0) - (a.year || 0);
          case 'titleAsc':
            return a.title.localeCompare(b.title);
          case 'titleDesc':
            return b.title.localeCompare(a.title);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [animes, searchTerm, viewMode, selectedYear, selectedSeason, filterRank, filterStatus, sortBy, allRatings]);

  return {
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    selectedYear,
    setSelectedYear,
    selectedSeason,
    setSelectedSeason,
    filterRank,
    setFilterRank,
    filterStatus,
    setFilterStatus,
    sortBy,
    setSortBy,
    activeFilter,
    setActiveFilter,
    years,
    allRatings,
    suggestionsList,
    filteredAnimes
  };
};